"use client";

/**
 * ChatInterface - Rebuilt from First Principles
 *
 * Clean architecture with hooks handling all complexity:
 * - useChatSync: Syncs useChat messages to store
 * - useServerSync: Handles server synchronization
 * - Logger: LLM-friendly logging for debugging
 *
 * This component is now thin - just UI orchestration.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useChat } from "@ai-sdk/react";
import { Alert, Tooltip } from "@/ui";
import { colors, spacing, textStyles, layout } from "@/ui/tokens";
import { AnimatePresence, motion } from "framer-motion";
import { SOLATransport } from "@/lib/sola/transport";
import { MessageList } from "./MessageList";
import { Greeting } from "./Greeting";
import { InputArea } from "./InputArea";
import { ContextBar } from "./Floating/ContextBar";
import { useChatStore, selectMessageIds, selectSettingsOpen, selectDebugMode } from "@/lib/sola/store";
import { useChatSync } from "@/lib/sola/hooks/useChatSync";
import { logger } from "@/lib/sola/logger";
import type { SolaMessage, SolaContext, SolaVisibilityType, SolaUsage } from "@/lib/sola/types";
import { SOLA_CONFIG } from "@/lib/sola/config";
import { useAttachments } from "@/lib/sola/attachments/useAttachments";
import { toFileUIParts } from "@/lib/sola/attachments/toFileUIParts";
import { ContentCopy, Check, Casino } from "@mui/icons-material";
import { IconButton } from "../Tool/IconButton";
import { SettingsDialog } from "../ToolDisplays/judge/SettingsDialog";
import chatStyles from "./ChatInterface.module.css";
import { QuickReplyProvider } from "../QuickReplyContext";

interface PageContext {
  pageType: string;
  entityId?: string;
  route?: string;
  availableActions?: string[];
}

interface ChatInterfaceProps {
  initialMessages?: SolaMessage[];
  context?: SolaContext;
  visibilityType?: SolaVisibilityType;
  className?: string;
  onTransportReady?: (transport: any) => void;
  onNewChatReady?: (handler: () => void) => void;
  pageContext?: PageContext;
}

function SOLAChatCore({
  initialMessages = [],
  context = {},
  visibilityType = "private",
  className,
  onTransportReady,
  onNewChatReady,
  pageContext,
}: ChatInterfaceProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const debugMode = useChatStore(selectDebugMode);
  const settingsOpen = useChatStore(selectSettingsOpen);
  const setSettingsOpen = useChatStore((s) => s.setSettingsOpen);
  const setDebugMode = useChatStore((s) => s.setDebugMode);

  const [copied, setCopied] = useState(false);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);

  // Hydrate debugMode from localStorage after mount to avoid SSR mismatch
  useEffect(() => {
    const stored = localStorage.getItem('sola-debug-mode');
    if (stored === 'true') setDebugMode(true);
  }, [setDebugMode]);

  const handleDebugToggle = (enabled: boolean) => {
    setDebugMode(enabled);
    logger.lifecycle("Debug mode", { enabled });
  };

  const handleCopyMessages = useCallback(async () => {
    const { messageIds, messagesById } = useChatStore.getState();
    const msgs = messageIds.map((id) => messagesById[id]).filter(Boolean);
    try {
      await navigator.clipboard.writeText(JSON.stringify(msgs, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy messages:", err);
    }
  }, []);

  const handleRandomPrompt = useCallback(async () => {
    setGeneratingPrompt(true);
    try {
      const res = await fetch("/api/debug/random-prompt", { method: "POST" });
      const data = await res.json();
      if (data.prompt) {
        setInput(data.prompt);
        inputRef.current?.focus();
      }
    } catch (err) {
      console.error("Failed to generate random prompt:", err);
    } finally {
      setGeneratingPrompt(false);
    }
  }, []);

  const messageIds = useChatStore(selectMessageIds);

  const setUsage = useChatStore((state) => state.setUsage);

  const transport = useMemo(() => {
    return new SOLATransport({}, visibilityType);
  }, [visibilityType]);

  useEffect(() => {
    if (transport && onTransportReady) {
      logger.lifecycle("Notifying parent about transport ready");
      onTransportReady(transport);
    }
  }, [transport, onTransportReady]);

  useEffect(() => {
    if (transport && transport.updateContext) {
      transport.updateContext(context);
    }
  }, [transport, context]);

  const {
    messages,
    sendMessage,
    setMessages,
    error,
    status,
    stop,
  } = useChat<SolaMessage>({
    id: "sola-chat",
    // v7: initial messages are seeded via `messages` (was `initialMessages` in v6).
    messages: initialMessages,
    transport,
    experimental_throttle: SOLA_CONFIG.throttle,
    onData: (dataPart) => {
      if (dataPart.type === "data-usage") {
        setUsage(dataPart.data as SolaUsage);
      }
    },
    onError: (error) => {
      logger.error("Chat error", error as Error, {
        errorMessage: error?.message,
        errorStack: error?.stack,
        errorDetails: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      });
    },
    onFinish: async ({ message }) => {
      logger.lifecycle("Chat onFinish", {
        hasMessage: !!message,
        messageId: message?.id,
      });
      // NOTE: Assistant messages are saved by API route
    },
  });

  useChatSync(messages, status);

  const attachments = useAttachments({
    conversationId: (context as { conversationId?: string | null })?.conversationId ?? null,
  });

  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragCounter = useRef(0);

  // Expose new chat handler that clears both useChat and the Zustand store
  const clearMessages = useChatStore((s) => s.clearMessages);
  useEffect(() => {
    if (onNewChatReady) {
      onNewChatReady(() => {
        setMessages([]);
        clearMessages();
      });
    }
  }, [onNewChatReady, setMessages, clearMessages]);

  const isLoading = status === "streaming" || status === "submitted";

  // Auto-focus input after response completes
  useEffect(() => {
    if (status === "ready" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading || attachments.isUploading) return;

    const fileParts = toFileUIParts(attachments.pending);
    const trimmed = input.trim();

    if (!trimmed && fileParts.length === 0) return;

    logger.user("Message sent", {
      length: trimmed.length,
      attachments: fileParts.length,
    });

    const parts: SolaMessage["parts"] = [];
    fileParts.forEach((fp) => {
      parts.push({
        type: "file",
        url: fp.url,
        mediaType: fp.mediaType,
        filename: fp.filename ?? "attachment",
        name: fp.filename ?? "attachment",
        ...((fp as { providerMetadata?: unknown }).providerMetadata
          ? { providerMetadata: (fp as { providerMetadata?: unknown }).providerMetadata }
          : {}),
      } as SolaMessage["parts"][number]);
    });
    if (trimmed) {
      parts.push({ type: "text", text: trimmed });
    }

    sendMessage({
      role: "user",
      parts,
    });
    setInput("");
    attachments.clear();
  };

  const handleQuestionClick = (question: string) => {
    logger.user("Greeting question clicked", { question });
    // Suggested-action prompts are long; flag the message as hidden so the
    // bubble isn't shown. The full prompt is still sent to the model, so the
    // first thing the user sees after clicking is SOLA's response.
    sendMessage({
      role: "user",
      parts: [{ type: "text", text: question }],
      metadata: { hidden: true },
    });
  };

  const handleQuickReply = useCallback(
    (text: string, metadata?: Record<string, unknown>) => {
      sendMessage({
        role: "user",
        parts: [{ type: "text", text }],
        ...(metadata ? { metadata } : {}),
      });
    },
    [sendMessage]
  );

  const quickReplyValue = useMemo(
    () => ({ sendMessage: handleQuickReply }),
    [handleQuickReply]
  );

  const handleDragEnter = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types ?? []).includes("Files")) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDraggingFiles(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDraggingFiles(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types ?? []).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types ?? []).includes("Files")) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsDraggingFiles(false);

    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) {
      attachments.add(files);
    }
  };

  return (
    <QuickReplyProvider value={quickReplyValue}>
    <div
      className={className}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.canvas.default,
        position: "relative",
      }}
    >
      {/* Drag-and-drop overlay */}
      <AnimatePresence>
        {isDraggingFiles && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              backgroundColor: "rgba(19, 49, 154, 0.08)",
              border: `2px dashed ${colors.action.product}`,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              backdropFilter: "blur(2px)",
            }}
          >
            <div
              style={{
                ...textStyles.body1,
                color: colors.action.product,
                fontWeight: 600,
                background: colors.canvas.default,
                padding: `${spacing[3]}px ${spacing[5]}px`,
                borderRadius: 999,
                border: `1px solid ${colors.action.product}`,
              }}
            >
              Drop files to attach
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div style={{ maxWidth: layout.chatColumnWidth, margin: "0 auto", paddingTop: spacing[4] }}>
              <Alert variant="error">
                {error?.message || "An error occurred"}
              </Alert>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
          minHeight: 0,
        }}
      >
        {messageIds.length === 0 ? (
          <div
            style={{
              height: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              paddingLeft: spacing[4],
              paddingRight: spacing[4],
            }}
          >
            <div
              style={{
                maxWidth: layout.chatColumnWidth,
                margin: "auto",
                width: "100%",
              }}
            >
              <Greeting onQuestionClick={handleQuestionClick} />
            </div>
          </div>
        ) : (
          <div style={{ height: "100%", width: "100%" }}>
            <MessageList
              messageIds={messageIds}
              isLoading={isLoading}
              liveMessages={isLoading ? messages : undefined}
            />
          </div>
        )}
      </div>

      {/* Context Bar */}
      {pageContext && pageContext.pageType !== "unknown" && (
        <ContextBar context={pageContext} />
      )}

      {/* Debug toolbar - only visible in debug mode */}
      {debugMode && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacing[1],
            paddingLeft: spacing[4],
            paddingRight: spacing[4],
            paddingTop: spacing[1],
            paddingBottom: spacing[1],
            borderTop: `1px solid ${colors.border.subtle}`,
            backgroundColor: colors.action.productLight,
          }}
        >
          <span
            style={{
              ...textStyles.caption,
              color: colors.text.muted,
              marginRight: spacing[1],
            }}
          >
            Debug:
          </span>
          <Tooltip content={copied ? "Copied!" : "Copy chat JSON"}>
            <IconButton onClick={handleCopyMessages}>
              {copied ? (
                <Check style={{ fontSize: 16, color: colors.signal.success }} />
              ) : (
                <ContentCopy style={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip content="Generate random prompt">
            <span>
              <IconButton
                onClick={handleRandomPrompt}
                disabled={generatingPrompt || isLoading}
              >
                <Casino
                  style={{ fontSize: 16 }}
                  className={generatingPrompt ? chatStyles.spinning : undefined}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}

      {/* Input area */}
      <InputArea
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onSubmit={handleSubmit}
        onStop={stop}
        disabled={isLoading}
        attachments={attachments}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        debugMode={debugMode}
        onDebugToggle={handleDebugToggle}
      />

    </div>
    </QuickReplyProvider>
  );
}

export function ChatInterface(props: ChatInterfaceProps) {
  return <SOLAChatCore {...props} />;
}
